// Reward feedback: confetti, haptics, procedural Web Audio SFX.
// All respect the profile store's soundMuted / hapticsEnabled flags.

import confetti from "canvas-confetti";
import { useProfileStore } from "@/state/profile";

let audioCtx: AudioContext | null = null;
function ctx(): AudioContext | null {
  if (typeof window === "undefined") return null;
  if (!audioCtx) {
    const Ctor =
      (window as unknown as { AudioContext?: typeof AudioContext; webkitAudioContext?: typeof AudioContext })
        .AudioContext ??
      (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctor) return null;
    audioCtx = new Ctor();
  }
  return audioCtx;
}

function beep(
  freq: number,
  durationMs: number,
  type: OscillatorType = "triangle",
  volume = 0.14,
  detuneCents = 0,
): void {
  const ac = ctx();
  if (!ac) return;
  const osc = ac.createOscillator();
  const gain = ac.createGain();
  osc.type = type;
  osc.frequency.value = freq;
  osc.detune.value = detuneCents;
  gain.gain.value = 0;
  const t0 = ac.currentTime;
  gain.gain.setValueAtTime(0, t0);
  gain.gain.linearRampToValueAtTime(volume, t0 + 0.01);
  gain.gain.exponentialRampToValueAtTime(0.0001, t0 + durationMs / 1000);
  osc.connect(gain).connect(ac.destination);
  osc.start(t0);
  osc.stop(t0 + durationMs / 1000 + 0.05);
}

function playSequence(
  steps: { f: number; d: number; delay?: number; type?: OscillatorType; vol?: number }[],
): void {
  let t = 0;
  for (const s of steps) {
    setTimeout(() => beep(s.f, s.d, s.type ?? "triangle", s.vol), t);
    t += s.delay ?? s.d;
  }
}

function haptic(pattern: number | number[]): void {
  if (typeof navigator === "undefined" || !("vibrate" in navigator)) return;
  try {
    (navigator as Navigator).vibrate(pattern);
  } catch {
    /* noop */
  }
}

function flags() {
  const s = useProfileStore.getState();
  return { muted: s.soundMuted, haptics: s.hapticsEnabled };
}

// ─── public API ──────────────────────────────────────────────

export function sfxCorrect(combo = 1): void {
  const { muted, haptics } = flags();
  if (haptics) haptic(20);
  if (muted) return;
  // Higher pitch as combo climbs — first five add real lift, then levels off.
  const boost = Math.min(combo - 1, 8) * 60;
  playSequence([
    { f: 660 + boost, d: 110, type: "sine", vol: 0.16 },
    { f: 880 + boost, d: 160, type: "sine", vol: 0.18, delay: 90 },
  ]);
}

export function sfxWrong(): void {
  const { muted, haptics } = flags();
  if (haptics) haptic([60, 40, 60]);
  if (muted) return;
  playSequence([
    { f: 300, d: 120, type: "sawtooth", vol: 0.12 },
    { f: 200, d: 260, type: "sawtooth", vol: 0.12, delay: 70 },
  ]);
}

export function sfxComplete(): void {
  const { muted, haptics } = flags();
  if (haptics) haptic([15, 40, 15, 40, 40]);
  if (muted) return;
  playSequence([
    { f: 523, d: 110, type: "triangle" },
    { f: 659, d: 110, type: "triangle", delay: 90 },
    { f: 784, d: 140, type: "triangle", delay: 90 },
    { f: 1047, d: 220, type: "triangle", delay: 120 },
  ]);
}

export function burstConfetti(color = "#FF5E5B"): void {
  confetti({
    particleCount: 60,
    startVelocity: 38,
    spread: 70,
    origin: { y: 0.4 },
    colors: [color, "#FFB627", "#26C3A5", "#7B61FF", "#3B9DFF"],
    scalar: 0.9,
    ticks: 140,
  });
}

export function bigCelebration(): void {
  confetti({
    particleCount: 160,
    spread: 110,
    startVelocity: 55,
    origin: { y: 0.35 },
    colors: ["#FF5E5B", "#FFB627", "#26C3A5", "#7B61FF", "#3B9DFF", "#FFFFFF"],
    scalar: 1.1,
    ticks: 220,
  });
}
